/**
 * Staff notification center helpers (tracker item 62): where an alert's link
 * opens, the badge text and the "x ago" label. Pure functions.
 */
import { StaffNotificationLink } from '../api/staffNotificationTypes';

export const STAFF_NOTIFICATIONS_PATH = '/notifications';
export const STAFF_ALERT_PREFERENCES_PATH = '/settings/my-alerts';
/** Fired when the list changed (read state), so the header badge refreshes at once */
export const STAFF_NOTIFICATIONS_CHANGED = 'crm:staff-notifications-changed';

export function notifyStaffNotificationsChanged(): void {
  try {
    window.dispatchEvent(new Event(STAFF_NOTIFICATIONS_CHANGED));
  } catch {
    // Non-essential
  }
}

/** The page a link opens, or null for a link kind this build does not know (the alert is then not clickable). */
export function staffNotificationPath(link: StaffNotificationLink | null | undefined): string | null {
  if (!link || !link.id) return null;
  const id = encodeURIComponent(link.id);
  switch (link.type) {
    case 'SERVICE_REQUEST':
      return `/requests/service/${id}`;
    case 'RESCHEDULE_REQUEST':
      // Reschedule requests are answered in the Requests inbox (no page per request)
      return '/requests';
    case 'CONVERSATION':
      return `/inbox/${id}`;
    case 'INVOICE':
      return `/invoices/${id}`;
    case 'PAYMENT':
      return `/payments/${id}`;
    case 'ESTIMATE':
      return `/estimates/${id}`;
    case 'JOB':
      return `/jobs/${id}`;
    case 'CUSTOMER':
      return `/customers/${id}`;
    case 'ENROLLMENT':
      // Service bundles (tracker item 85): a pending renewal or request, or a blocked renewal's enrollment
      return `/enrollments/${id}`;
    default:
      return null;
  }
}

/** "3" or "99+" (the server caps the count at 100). */
export function badgeText(unread: number, capped = false): string {
  return capped || unread > 99 ? '99+' : String(unread);
}

/** "just now", "5 min ago", "3 h ago", "2 d ago", else the date. */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((now.getTime() - then) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString();
}
